import get from 'lodash/get';
import React from 'react';
import { Controller } from 'react-hook-form';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Radio } from '../../../../../../CommonComponents/OPL/Radio/Radio';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { DistributionReasonDTO } from '../../../../api/useWithdrawalOptionsQuery';
import { useWithdrawSelection } from './useWithdrawSelection';
import {
  DISTRIBUTION_REASONS_TITLES,
  PRIMARY_DISTRIBUTION_REASON_DESCRIPTION,
} from './WithdrawSelection.meta';

interface Props {
  onClose(): void;
  onBack(): void;
  onDistributionReasonChange(distributionReason: DistributionReasonDTO): void;
}

export const WithdrawSelection: React.FC<Props> = ({
  onClose,
  onBack,
  onDistributionReasonChange,
}) => {
  const {
    data,
    actions: { onSubmit },
  } = useWithdrawSelection({ onDistributionReasonChange });

  const {
    formBag,
    isOtherDistributionSelected,
    primaryDistributionReasons,
    otherDistributionReasons,
  } = data;

  const {
    formState: { errors },
  } = formBag;

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>Select IRA Withdrawal Type</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <form onSubmit={formBag.handleSubmit(onSubmit)}>
        <ModalBody css="padding-bottom: 2px">
          <Controller
            control={formBag.control}
            name="distributionReason"
            render={({ field: { value: reason } }) => {
              return (
                <Box display="flex" flexDirection="column">
                  {primaryDistributionReasons.map((primaryReason, index) => (
                    <Radio
                      key={primaryReason.value}
                      checked={primaryReason.value === reason?.value}
                      onChange={() => formBag.setValue('distributionReason', primaryReason)}
                      helperText={PRIMARY_DISTRIBUTION_REASON_DESCRIPTION[primaryReason.value]}
                      mt={index !== 0 ? '16' : 0}
                    >
                      {DISTRIBUTION_REASONS_TITLES[primaryReason.value]}
                    </Radio>
                  ))}
                </Box>
              );
            }}
          />

          <Controller
            control={formBag.control}
            name="distributionReason"
            render={() => {
              return (
                <Radio
                  checked={isOtherDistributionSelected}
                  onChange={() => formBag.setValue('distributionReason', null)}
                  helperText="I would like to make some other type of IRA distribution, due to circumstances such as the disability or death of the account holder."
                  mt="16"
                >
                  Other
                </Radio>
              );
            }}
          />

          {isOtherDistributionSelected && (
            <Controller
              control={formBag.control}
              name="distributionReason"
              render={({ field: { value } }) => {
                return (
                  <Box ml="24" mt="4">
                    <Select
                      value={value}
                      onChange={opt => {
                        formBag.setValue('distributionReason', opt);
                        formBag.clearErrors('distributionReason');
                      }}
                      options={otherDistributionReasons}
                      formVariant={get(errors, 'distributionReason') ? 'error' : undefined}
                      helperText={get(errors, 'distributionReason.message')}
                      isPortal
                      aria-label="Select other distribution reason"
                    />
                  </Box>
                );
              }}
            />
          )}
        </ModalBody>

        <ModalFooter>
          <Box flex={1}>
            <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
              Back
            </Button>
          </Box>

          <Button variant="subtleInactive" onClick={onClose} mr="8">
            Cancel
          </Button>

          <Button type="submit">Next</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
